import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const PrescriptionModal = ({ isOpen, onClose, prescriptionData, productName, ca_id }) => {
  // Clean up blob URLs when component unmounts or when modal closes
  useEffect(() => {
    return () => {
      if (prescriptionData?.fileUrl) {
        URL.revokeObjectURL(prescriptionData.fileUrl);
      }
    };
  }, [prescriptionData]);

  if (!isOpen || !prescriptionData) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-900 to-blue-800 p-6 text-white">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Prescription Details</h2>
              <button
                onClick={onClose}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-blue-100 text-sm mt-2">
              {productName && `For: ${productName}`}
            </p>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Display prescription file if available */}
            {prescriptionData.fileUrl && (
              <div className="mb-6 border rounded-lg overflow-hidden">
                {prescriptionData.fileType === 'local/file' ? (
                  <div className="p-4 text-center">
                    <p className="text-gray-600 mb-2">Prescription File</p>
                    <p className="text-sm text-gray-500 mb-3">
                      {prescriptionData.prescriptionFilePath}
                    </p>
                    <p className="text-sm text-amber-600 mb-3">
                      Note: This is a local file path. To view this file, please contact support.
                    </p>
                  </div>
                ) : prescriptionData.fileType.startsWith('image/') ? (
                  <img 
                    src={prescriptionData.fileUrl} 
                    alt="Prescription" 
                    className="w-full h-auto max-h-[400px] object-contain mx-auto"
                    onError={(e) => {
                      // If image fails to load, show a fallback message
                      e.target.style.display = 'none';
                      const container = e.target.parentNode;
                      const fallback = document.createElement('div');
                      fallback.className = 'p-4 text-center';
                      fallback.innerHTML = `
                        <p class="text-gray-600 mb-2">Unable to load image</p>
                        <p class="text-sm text-gray-500">${prescriptionData.fileUrl}</p>
                        <p class="text-sm text-amber-600 mt-2">Please check the file path or contact support.</p>
                      `;
                      container.appendChild(fallback);
                    }}
                  />
                ) : (
                  <div className="p-4 text-center">
                    <p className="text-gray-600 mb-2">Prescription Document</p>
                    <a 
                      href={prescriptionData.fileUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                      onClick={(e) => {
                        // If it's a local path, prevent default and show a message
                        if (prescriptionData.isLocalPath) {
                          e.preventDefault();
                          alert('This is a local file path and cannot be opened directly. Please contact support.');
                        }
                      }}
                    >
                      {prescriptionData.isLocalPath ? 'Local File' : 'View Prescription File'}
                    </a>
                  </div>
                )}
              </div>
            )}
            
            {/* Only show eye prescription details if there's no file being displayed */}
            {!prescriptionData.fileUrl && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                {/* Right Eye */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm">R</span>
                    Right Eye
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">SPH:</span>
                      <span className="font-semibold">{prescriptionData.right?.sph || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">CYL:</span>
                      <span className="font-semibold">{prescriptionData.right?.cyl || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Axis:</span>
                      <span className="font-semibold">{prescriptionData.right?.axis || 'N/A'}</span>
                    </div>
                    {prescriptionData.right?.add && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Add:</span>
                        <span className="font-semibold">{prescriptionData.right.add}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Left Eye */}
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-sm">L</span>
                    Left Eye
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">SPH:</span>
                      <span className="font-semibold">{prescriptionData.left?.sph || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">CYL:</span>
                      <span className="font-semibold">{prescriptionData.left?.cyl || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Axis:</span>
                      <span className="font-semibold">{prescriptionData.left?.axis || 'N/A'}</span>
                    </div>
                    {prescriptionData.left?.add && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Add:</span>
                        <span className="font-semibold">{prescriptionData.left.add}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* PD Information - Only show if there's no file and PD data exists */}
            {!prescriptionData.fileUrl && (prescriptionData.pd || prescriptionData.rightPD || prescriptionData.leftPD) && ca_id !== 3 && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="font-semibold text-amber-800 mb-2">Pupillary Distance (PD)</div>
                {prescriptionData.pd ? (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">PD:</span>
                    <span className="font-bold text-amber-700">{prescriptionData.pd} mm</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {prescriptionData.rightPD && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Right PD:</span>
                        <span className="font-bold text-amber-700">{prescriptionData.rightPD} mm</span>
                      </div>
                    )}
                    {prescriptionData.leftPD && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Left PD:</span>
                        <span className="font-bold text-amber-700">{prescriptionData.leftPD} mm</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 p-4 flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PrescriptionModal;